import { useState } from 'react';
import {
  CO_ATTACHMENT_VISIBILITY_OPTIONS,
  coVisibilityBadgeLabel,
  coVisibilityFromAttachment,
  coVisibilityLabel,
  isChangeOrderInvoiceAttachment,
  updateChangeOrderAttachmentVisibility,
  type CoAttachmentVisibility,
} from '@/lib/changeOrderUtils';
import {
  FILE_OPEN_ERROR_MESSAGE,
  openProjectFileInNewTab,
  useSignedFileUrls,
} from '@/lib/storageSignedUrl';
import type { InstructionAttachment } from './InstructionAttachmentThumbnails';

export function isDisplayableChangeOrderAttachment(file: InstructionAttachment): boolean {
  return !isChangeOrderInvoiceAttachment(file);
}

function attachmentIcon(file: InstructionAttachment): string {
  const type = `${file.file_type || ''} ${file.file_name || ''}`.toLowerCase();
  if (type.includes('pdf')) return 'ri-file-pdf-line text-[#EF4444]';
  if (/\b(jpg|jpeg|png|webp|heic|gif|bmp)\b/.test(type)) return 'ri-image-line text-[#0891B2]';
  return 'ri-file-text-line text-[#6B7280]';
}

export function ChangeOrderVisibilityBadge({
  file,
}: {
  file: Pick<InstructionAttachment, 'photo_type' | 'is_client_visible'>;
}) {
  const scope = coVisibilityFromAttachment(file);
  return (
    <span className="inline-flex items-center rounded-md bg-[#F3F4F6] px-1.5 py-0.5 text-[10px] font-semibold text-[#374151]">
      {coVisibilityBadgeLabel(scope)}
    </span>
  );
}

export default function ChangeOrderAttachmentList({
  files,
  loading = false,
  error,
  onRetry,
  onDelete,
  onVisibilityUpdated,
  allowEditVisibility = true,
  emptyText = 'No attachments yet.',
}: {
  files: InstructionAttachment[];
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  onDelete?: (file: InstructionAttachment) => void;
  onVisibilityUpdated?: (fileId: string, next: { photo_type: string; is_client_visible: boolean }) => void;
  allowEditVisibility?: boolean;
  emptyText?: string;
}) {
  const displayFiles = files.filter(isDisplayableChangeOrderAttachment);
  const signedUrls = useSignedFileUrls(displayFiles);
  const [openError, setOpenError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [visibilityError, setVisibilityError] = useState('');

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-[#F9FAFB] px-3 py-2.5">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-sm"></i>
        <span className="text-xs text-[#6B7280]">Loading attachments...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2.5">
        <div className="flex items-start gap-2">
          <i className="ri-error-warning-line text-[#EF4444] text-sm mt-0.5"></i>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-[#EF4444]">Failed to load attachments</p>
            <p className="text-[11px] text-[#991B1B] break-words">{error}</p>
          </div>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="px-2.5 py-1.5 rounded-lg bg-white text-[#EF4444] text-[11px] font-bold cursor-pointer"
            >
              Retry
            </button>
          )}
        </div>
      </div>
    );
  }

  if (displayFiles.length === 0) {
    return (
      <p className="text-xs text-[#9CA3AF] bg-[#F9FAFB] rounded-xl px-3 py-2.5 border border-gray-100">
        {emptyText}
      </p>
    );
  }

  async function handleVisibilityChange(file: InstructionAttachment, next: CoAttachmentVisibility) {
    const current = coVisibilityFromAttachment(file);
    if (next === current) return;
    setSavingId(file.id);
    setVisibilityError('');
    const result = await updateChangeOrderAttachmentVisibility(file.id, next);
    setSavingId(null);
    if (result.error || !result.photo_type || result.is_client_visible == null) {
      setVisibilityError(result.error || 'Could not update attachment visibility.');
      return;
    }
    onVisibilityUpdated?.(file.id, {
      photo_type: result.photo_type,
      is_client_visible: result.is_client_visible,
    });
  }

  return (
    <div className="space-y-2">
      {(openError || visibilityError) && (
        <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-[#EF4444] text-xs">
          {visibilityError || openError}
        </div>
      )}
      {displayFiles.map((file) => {
        const scope = coVisibilityFromAttachment(file);
        return (
          <div key={file.id} className="flex items-start gap-3 rounded-xl border border-gray-100 bg-white px-3 py-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#F9FAFB] flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={`${attachmentIcon(file)} text-base`}></i>
            </div>
            <div className="flex-1 min-w-0 space-y-1.5">
              <p className="text-xs font-semibold text-[#1A1A1A] truncate">{file.file_name}</p>
              <div className="flex flex-wrap items-center gap-2">
                <ChangeOrderVisibilityBadge file={file} />
                <span className="text-[10px] text-[#6B7280]">{coVisibilityLabel(scope)}</span>
              </div>
              {allowEditVisibility && (
                <label className="block">
                  <span className="sr-only">Change visibility for {file.file_name}</span>
                  <select
                    value={scope}
                    disabled={savingId === file.id}
                    onChange={(e) => void handleVisibilityChange(file, e.target.value as CoAttachmentVisibility)}
                    className="w-full max-w-[220px] px-2 py-1.5 rounded-lg border border-gray-200 bg-white text-[11px] font-medium text-[#374151] cursor-pointer focus:outline-none focus:border-[#E8621A]"
                  >
                    {CO_ATTACHMENT_VISIBILITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {(signedUrls[file.id] || file.storage_path || file.file_url) && (
                <button
                  type="button"
                  onClick={() => {
                    void (async () => {
                      const result = await openProjectFileInNewTab(file);
                      setOpenError(result.ok ? '' : FILE_OPEN_ERROR_MESSAGE);
                    })();
                  }}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-[#374151] cursor-pointer"
                  title="Open attachment"
                >
                  <i className="ri-download-2-line text-sm"></i>
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(file)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] text-[#EF4444] cursor-pointer"
                  title="Remove attachment"
                >
                  <i className="ri-delete-bin-line text-sm"></i>
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
